import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import "./SetStreakGoal.scss";
import OnboardingHeader from "../../../components/Headers/OnboardingHeader/OnboardingHeader";
import {
  GOAL_ONBOARDING_STEPS,
  OnboardingSteps,
} from "../../../utils/constants";
import { formatCurrency } from "../../../utils/helpers";
import {
  getStoredSelectedCurrency,
  getStoredSelectedEarning,
  getStoredSelectedStreak,
  setStoredSelectedStreak,
} from "../../../utils/storage";
import useOnboardingSteps from "../../../hooks/useOnboardingSteps";
import useAnalytics from "../../../hooks/useAnalytics";
import PrimaryActionButton from "../../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import OnboardingContainer from "../../../components/onboarding/OnboardingContainer/OnboardingContainer";
import GoalTipBulbIcon from "../../../components/icons/GoalTipBulbIcon";
import HyperCoin from "../../../components/HyperCoin/HyperCoin";

const STREAK_OPTIONS = [
  { days: 7, labelKey: "streak_label_good", multiplier: 2 },
  {
    days: 14,
    labelKey: "streak_label_great",
    multiplier: 4,
    featured: true,
  },
  { days: 28, labelKey: "streak_label_unstoppable", multiplier: 8 },
] as const;

type StreakOption = (typeof STREAK_OPTIONS)[number];

type StreakOptionCardProps = {
  option: StreakOption;
  selected: boolean;
  onSelect: (days: number) => void;
};

function StreakOptionCard({
  option,
  selected,
  onSelect,
}: StreakOptionCardProps) {
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });
  const featured = "featured" in option && option.featured;

  return (
    <button
      type="button"
      className={`streak-option${selected ? " selected" : ""}${
        featured ? " featured" : ""
      }`}
      onClick={() => onSelect(option.days)}
    >
      {featured ? (
        <span className="most-popular-tag">
          🔥 {t("most_popular")}
        </span>
      ) : null}
      <span className="streak-option-main">
        <span className="streak-option-title">
          {t("n_day_streak", { days: option.days })}
        </span>
        <span className="streak-option-label">{t(option.labelKey)}</span>
      </span>
      {selected ? (
        <span className="streak-option-benefit">
          <Trans
            i18nKey="onboarding.streak_more_likely"
            values={{ multiplier: option.multiplier }}
            components={{ bold: <strong /> }}
          />
        </span>
      ) : null}
    </button>
  );
}

function SetStreakGoal() {
  const analytics = useAnalytics();
  const { goToStep } = useOnboardingSteps();
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });
  const storedEarning = getStoredSelectedEarning();
  const storedCurrency = getStoredSelectedCurrency();
  const [selectedStreak, setSelectedStreak] = useState(
    () => getStoredSelectedStreak() ?? 0,
  );

  useEffect(() => {
    if (!storedEarning || !storedCurrency) {
      goToStep(OnboardingSteps.SET_EARNINGS_GOALS);
    }
  }, [goToStep, storedCurrency, storedEarning]);

  if (!storedEarning || !storedCurrency) return null;

  const handleSetGoal = () => {
    if (!selectedStreak) return;
    setStoredSelectedStreak(selectedStreak);
    analytics.sendChooseRewardSelectionEvent(storedEarning);
    goToStep(OnboardingSteps.SETTING_EARNINGS_GOALS);
  };

  return (
    <OnboardingContainer className="MobileOnly position-relative d-flex flex-column gap-4 set-streak-goal-page">
      <OnboardingHeader
        amountActive={2}
        steps={GOAL_ONBOARDING_STEPS}
        onClick={() => goToStep(OnboardingSteps.SET_EARNINGS_GOALS)}
      />

      <div className="set-streak-goal-body">
        <div className="set-streak-goal-content">
          <h1 className="streak-goal-heading">
            {t("set_a_goal_to_win")}{" "}
            <HyperCoin className="heading-hyper-coin" />{" "}
            {formatCurrency(storedEarning)} {t("daily")}.
          </h1>

          <div className="streak-options">
            {STREAK_OPTIONS.map((option) => (
              <StreakOptionCard
                key={option.days}
                option={option}
                selected={selectedStreak === option.days}
                onSelect={setSelectedStreak}
              />
            ))}
          </div>
        </div>

        <div className="set-streak-goal-footer">
          <PrimaryActionButton
            size="lg"
            text={t("set_my_goal")}
            disabled={!selectedStreak}
            onClick={handleSetGoal}
          />
          <p className="streak-tip">
            <GoalTipBulbIcon />
            <span>{t("playing_everyday_improves_your_gaming_skill")}</span>
          </p>
        </div>
      </div>
    </OnboardingContainer>
  );
}

export default SetStreakGoal;
